import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, ArrowLeft, Instagram } from "lucide-react";
import { INSTAGRAM_URL } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null });

  const poll = useCallback(
    async (attempt = 0) => {
      if (!sessionId) {
        setState({ status: "no-session", data: null });
        return;
      }
      if (attempt >= 10) {
        setState({ status: "timeout", data: null });
        return;
      }
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (err) {
        console.error(err);
        setTimeout(() => poll(attempt + 1), 2500);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#FDFBF7] flex items-center justify-center px-6 py-12">
      <div className="max-w-xl w-full text-center" data-testid="success-page">
        {state.status === "checking" && (
          <div data-testid="success-checking">
            <Loader2 className="w-12 h-12 text-[#A01828] mx-auto animate-spin" strokeWidth={1.25} />
            <p className="mt-6 font-serif-display text-3xl font-light">Перевіряємо оплату...</p>
            <p className="mt-3 text-white/50 text-sm">Це займе кілька секунд</p>
          </div>
        )}

        {state.status === "paid" && (
          <div data-testid="success-confirmed">
            <CheckCircle2 className="w-16 h-16 text-[#A01828] mx-auto" strokeWidth={1.25} />
            <p className="overline mt-8">Бронювання підтверджено</p>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl font-light leading-tight">
              Дякуємо!<br />
              <span className="italic text-[#A01828]">Ми зв'яжемося з тобою</span>,<br />
              щоб підтвердити бронювання.
            </h1>
            <p className="mt-8 text-white/60 leading-relaxed max-w-md mx-auto">
              Очікуй повідомлення в обраному месенджері протягом дня. До зустрічі в студії!
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                data-testid="success-home-link"
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-8 h-12 text-xs uppercase tracking-[0.2em] hover:bg-white/5 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> На головну
              </Link>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="success-instagram-link"
                className="inline-flex items-center justify-center gap-2 bg-[#7A1220] hover:bg-[#A01828] text-white px-8 h-12 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                <Instagram className="w-4 h-4" /> Підписатися
              </a>
            </div>
          </div>
        )}

        {(state.status === "expired" || state.status === "timeout" || state.status === "error" || state.status === "no-session") && (
          <div data-testid="success-failed">
            <XCircle className="w-16 h-16 text-white/50 mx-auto" strokeWidth={1.25} />
            <h1 className="mt-8 font-serif-display text-4xl font-light">
              {state.status === "no-session" ? "Сесія не знайдена" : "Оплата не пройшла"}
            </h1>
            <p className="mt-4 text-white/60">
              {state.status === "no-session"
                ? "Повернись на головну, щоб забронювати заняття."
                : "Спробуй ще раз або напиши нам в Instagram."}
            </p>
            <Link
              to="/"
              data-testid="failed-home-link"
              className="mt-8 inline-flex items-center gap-2 bg-[#7A1220] hover:bg-[#A01828] text-white px-8 h-12 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Повернутися
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
